import { redirect } from '@tanstack/react-router';
import { findWorkspaceBySlug } from '@vizit/access';
import { ensureMeContext } from '@vizit/api';
import { FEATURE_FLAGS } from '@vizit/constants';
import { customBenchmarksAvailabilityQueryOptions, getFeatureFlag } from '@vizit/feature-flags';
import type { RouteContext } from '../../router-provider/types';

/**
 * Keeps benchmark pages unreachable while custom benchmarks are off, even by a pasted URL: sends the user to the
 * Library's Collections view instead, the place benchmarks are listed when they are on.
 */
export async function guardCustomBenchmarks(context: RouteContext, workspaceSlug: string) {
  if (!(await isCustomBenchmarksAvailable(context, workspaceSlug))) {
    throw redirect({
      to: '/w/$workspaceSlug/library/',
      params: { workspaceSlug },
      search: { view: 'collections' },
      replace: true,
    });
  }
}

export async function guardComparison(workspaceSlug: string, collectionId: string) {
  const isEnabled = await getFeatureFlag(FEATURE_FLAGS.COMPARISON_FE, false);

  if (!isEnabled) {
    throw redirectToCollection(workspaceSlug, collectionId);
  }
}

export async function guardExploreTrends(context: RouteContext, workspaceSlug: string, collectionId: string) {
  // Comparison first: with it off the redirect is decided, so the benchmarks check is not worth a request.
  const isEnabled =
    (await getFeatureFlag(FEATURE_FLAGS.COMPARISON_FE, false)) &&
    (await isCustomBenchmarksAvailable(context, workspaceSlug));

  if (!isEnabled) {
    throw redirectToCollection(workspaceSlug, collectionId);
  }
}

/**
 * The loader-time `useCustomBenchmarksAvailability`: the flag, then the API for the workspace's organisation, through
 * the same cached query. It waits for a confirmed answer, retries of an "off" included, so the page never opens and
 * then bounces; the wait is only long where the answer is off, which a link in the app does not lead to. The workspace
 * comes from `/me` rather than the route context, which a direct load runs before the context carries it.
 */
async function isCustomBenchmarksAvailable(context: RouteContext, workspaceSlug: string) {
  const isFlagOn = await getFeatureFlag(FEATURE_FLAGS.CUSTOM_BENCHMARKS_FE, false);

  if (!isFlagOn) {
    return false;
  }

  const me = await ensureMeContext(context.queryClient, context.auth?.userId);
  // By slug only: falling back to another workspace would check an organisation the URL does not name.
  const workspaceId = findWorkspaceBySlug(me, workspaceSlug)?.id;

  if (!workspaceId) {
    return false;
  }

  return context.queryClient.fetchQuery(customBenchmarksAvailabilityQueryOptions(context.queryClient, workspaceId));
}

function redirectToCollection(workspaceSlug: string, collectionId: string) {
  return redirect({
    to: '/w/$workspaceSlug/library/collections/$collectionId',
    params: { workspaceSlug, collectionId },
    replace: true,
  });
}
